import type { ReactNode } from 'react'
import AnimatedItem from '@/components/tailwindadmin/components/animated-component/ListAnimation'
import { MeasureAccordionItem } from './MeasureAccordionItem'
import type { MeasureChartData, DetailMode } from '@/lib/equip-types'

interface MeasureAccordionProps {
  measures: MeasureChartData[]
  detailMode: DetailMode
  isDrilldownLoading: boolean
  drilldownError: string | null
  onDismissError?: () => void
  onMeasureClick: (measureName: string, measureType: string) => void
  renderGrid: (measure: MeasureChartData, detailMode: DetailMode) => ReactNode
}

function getExpandedMeasureName(detailMode: DetailMode): string | null {
  switch (detailMode.type) {
    case 'quality-measure':
      return detailMode.measureName
    case 'pims':
      return 'PIMs'
    case 'audit':
      return detailMode.auditType === 'coding' ? 'Coding' : 'Quality_Audit'
    case 'ed-sendout':
      return 'ED_SendOut'
    default:
      return null
  }
}

export function MeasureAccordion({
  measures,
  detailMode,
  isDrilldownLoading,
  drilldownError,
  onDismissError,
  onMeasureClick,
  renderGrid,
}: MeasureAccordionProps) {
  const expandedName = getExpandedMeasureName(detailMode)

  if (!measures || measures.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No measures available</p>
      </div>
    )
  }

  return (
    <div className="space-y-2.5">
      {measures.map((measure, index) => {
        const isExpanded = expandedName === measure.measureName
        return (
          <AnimatedItem key={measure.measureName} index={index}>
            <MeasureAccordionItem
              measure={measure}
              isExpanded={isExpanded}
              isDrilldownLoading={isExpanded && isDrilldownLoading}
              drilldownError={isExpanded ? drilldownError : null}
              onDismissError={onDismissError}
              onMeasureClick={onMeasureClick}
            >
              {isExpanded && !isDrilldownLoading ? renderGrid(measure, detailMode) : null}
            </MeasureAccordionItem>
          </AnimatedItem>
        )
      })}
    </div>
  )
}
